import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportError } from '@/services/errorTelemetry';

interface Props { children: ReactNode }
interface State { hasError: boolean }

/**
 * Top-level boundary that catches render-time React errors and ships
 * them to the Errors dashboard with the component stack. The chat
 * surface itself shouldn't keep running after a render crash, so we
 * present a one-line fallback with a refresh button.
 */
export class RootErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      area: 'react-render',
      componentStack: info.componentStack ?? undefined,
    }, 'error');
  }

  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" className="flex h-screen items-center justify-center p-8 text-center">
          <div>
            <h1 className="text-lg font-semibold text-text-primary">Something went wrong.</h1>
            <p className="mt-2 text-sm text-text-secondary">
              The error has been reported. Refresh the page to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 rounded-md bg-stellar-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
